import React, {useCallback, useEffect, useState} from "react";
import {Box, Grid} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {Line} from "react-chartjs-2";
import PrescriptionService from "@user-service/PrescriptionService";

/**
 * EEG 측정 섹션 하단 - 개안/폐안 FFT 평균 라인 그래프
 *
 * getFFTAvgBySection API 사용
 * x축: 0 ~ 40 Hz
 * y축: mV (각 40초 구간의 Hz별 평균)
 * 왼쪽: 개안 (0~40초 평균), 오른쪽: 폐안 (40~80초 평균)
 */
export default function CurrentRawEEG({measureResult}) {
    const classes = useStyles();
    const {id, isEEG} = measureResult;
    const [fftAvg, setFftAvg] = useState(null);

    const fetch = useCallback(async () => {
        try {
            const data = await PrescriptionService.getFFTAvgBySection(id);
            if (data) {
                setFftAvg(data);
            }
        } catch (e) {}
    }, [id]);

    useEffect(() => {
        if ('yes' === isEEG) {
            fetch().then();
        }
    }, [fetch, isEEG]);

    return (
        <Grid container spacing={1} style={{marginTop: 4}}>
            <Grid item xs={6}>
                <Box className={classes.chartBox}>
                    <Typography variant={'subtitle2'} className={classes.label}>
                        개안 (Eyes Open) 0 ~ 40초 평균
                    </Typography>
                    <FFTAvgLineChart
                        ch1Data={fftAvg ? fftAvg.openEyes.ch1 : []}
                        ch2Data={fftAvg ? fftAvg.openEyes.ch2 : []}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box className={classes.chartBox}>
                    <Typography variant={'subtitle2'} className={classes.label}>
                        폐안 (Eyes Closed) 40 ~ 80초 평균
                    </Typography>
                    <FFTAvgLineChart
                        ch1Data={fftAvg ? fftAvg.closedEyes.ch1 : []}
                        ch2Data={fftAvg ? fftAvg.closedEyes.ch2 : []}
                    />
                </Box>
            </Grid>
        </Grid>
    );
}

/**
 * Hz별 평균 mV 라인 차트
 * x축: 0~39 Hz, y축: mV
 * 데이터가 없어도 빈 차트 프레임 표시
 */
function FFTAvgLineChart({ch1Data, ch2Data}) {
    const hzCount = 40;
    const labels = Array.from({length: hzCount}, (_, i) => `${i}Hz`);

    const ch1Values = ch1Data && ch1Data.length > 0 ? ch1Data : [];
    const ch2Values = ch2Data && ch2Data.length > 0 ? ch2Data : [];

    const data = {
        labels,
        datasets: [
            {
                label: '좌뇌(Left)',
                data: ch1Values,
                fill: false,
                backgroundColor: 'rgba(0,178,245,0.5)',
                borderColor: 'rgba(0,178,245,1)',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 3,
                pointHitRadius: 5,
            },
            {
                label: '우뇌(Right)',
                data: ch2Values,
                fill: false,
                backgroundColor: 'rgba(199,116,210,0.5)',
                borderColor: 'rgba(199,116,210,1)',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 3,
                pointHitRadius: 5,
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                fontColor: '#333',
                fontSize: 11,
                padding: 12,
                usePointStyle: true,
            }
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0,0,0,0.7)',
            titleFontSize: 12,
            bodyFontSize: 11,
            callbacks: {
                label: function (tooltipItem, chartData) {
                    const label = chartData.datasets[tooltipItem.datasetIndex].label;
                    return `${label}: ${Number(tooltipItem.yLabel).toFixed(2)} mV`;
                }
            }
        },
        scales: {
            xAxes: [{
                display: true,
                gridLines: {
                    display: true,
                    color: 'rgba(0,0,0,0.05)',
                },
                ticks: {
                    fontColor: '#666',
                    fontSize: 10,
                    maxTicksLimit: 10,
                    maxRotation: 0,
                },
                scaleLabel: {
                    display: true,
                    labelString: 'Hz',
                    fontColor: '#666',
                    fontSize: 11,
                }
            }],
            yAxes: [{
                display: true,
                gridLines: {
                    display: true,
                    color: 'rgba(0,0,0,0.05)',
                },
                ticks: {
                    fontColor: '#666',
                    fontSize: 10,
                    suggestedMin: 0,
                    suggestedMax: 50,
                    beginAtZero: true,
                },
                scaleLabel: {
                    display: true,
                    labelString: 'mV',
                    fontColor: '#666',
                    fontSize: 11,
                }
            }]
        }
    };

    return (
        <Box style={{height: 190, position: 'relative'}}>
            <Line
                key={Math.random()}
                data={data}
                options={options}
            />
        </Box>
    );
}

const useStyles = makeStyles(() => ({
    chartBox: {
        backgroundColor: '#fff',
        borderRadius: 6,
        padding: '6px 10px 4px',
    },
    label: {
        fontWeight: 'bold',
        color: '#555',
        marginBottom: 4,
        paddingLeft: 2,
        fontSize: 12,
    },
}));
